import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
  Divider,
  Fade,
  Slide,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ImageSlideshow from "../components/ImageSlideshow";
import SEOHead from "../components/SEOHead";
import CitabilitySection from "../components/CitabilitySection";
import {
  ArrowForward as ArrowForwardIcon,
  AccessTime as AccessTimeIcon,
  Download as DownloadIcon,
  Spa as SpaIcon,
  LocalFlorist as LocalFloristIcon,
  Nature as NatureIcon,
} from "@mui/icons-material";
import { getClasses, Class as ClassType } from "../services/api";
import axios from "axios";
import { API_URL } from "../config";

const Home: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));

  let slideshowHeight = 600;
  if (isXs) slideshowHeight = 250;
  else if (isSm) slideshowHeight = 400;

  const [classes, setClasses] = useState<ClassType[]>([]);
  const [portfolioPdf, setPortfolioPdf] = useState<string | null>(null);

  useEffect(() => {
    getClasses()
      .then(setClasses)
      .catch(() => {
        // Error loading classes
      });
    
    // Fetch portfolio PDF
    axios
      .get(`${API_URL}/api/portfolio`)
      .then((response) => {
        if (response.data.pdf) {
          setPortfolioPdf(response.data.pdf);
        }
      })
      .catch(() => {
        console.error("Failed to fetch portfolio");
      });
  }, []);

  const featuredClasses = classes.slice(0, 3);

  // Find first class of each type for slideshow links
  const firstCandleClass = classes.find((c) => c.type === "candle");
  const firstPlantClass = classes.find((c) => c.type === "plant");

  // Slideshow only needs the two existing images in /public/images/slideshow
  const slideshowData = useMemo(
    () => [
      {
        id: "candle",
        image: "/images/slideshow/candle-making.jpg",
        title: "蠟燭製作工作坊",
        description: "使用精選材料製作美麗的個人化蠟燭",
        link: firstCandleClass ? `/classes/${firstCandleClass._id}` : undefined,
      },
      {
        id: "plant",
        image: "/images/slideshow/plant-care.jpg",
        title: "植物護理課程",
        description: "學習如何照顧和培育室內植物",
        link: firstPlantClass ? `/classes/${firstPlantClass._id}` : undefined,
      },
    ],
    [firstCandleClass, firstPlantClass]
  );

  // Enhanced structured data for AI search engines
  const structuredData = useMemo(() => {
    const baseUrl = "https://cosylabdesigns.com";
    const data: any[] = [
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": `${baseUrl}/#organization`,
        name: "CosyLab",
        alternateName: "CosyLab Craft",
        description:
          "一站式到校課程服務：CosyLab 提供專業到校興趣班、學校方案、課後興趣班。包括蠟燭製作、植物護理等手工藝到校課程，可長期駐校開班，適合中小學及成人團體參與。",
        url: baseUrl,
        logo: `${baseUrl}/icon/icon.png`,
        image: `${baseUrl}/icon/icon.png`,
        telephone: "+85284378839",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Hong Kong",
          addressRegion: "Hong Kong",
          addressCountry: "HK",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: "22.3193",
          longitude: "114.1694",
        },
        areaServed: {
          "@type": "City",
          name: "Hong Kong",
        },
        sameAs: [
          "https://wa.me/85284378839",
          "https://www.instagram.com/cosylab.craft/",
          "https://www.facebook.com/cosylab.craft/",
          // Add social media links if available
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "@id": `${baseUrl}/#educational`,
        name: "CosyLab",
        parentOrganization: {
          "@type": "LocalBusiness",
          "@id": `${baseUrl}/#organization`,
        },
        
        description:
          "一站式到校課程教育機構：提供專業到校興趣班、學校方案、課後興趣班服務。包括手工藝到校課程、長期駐校開班、等一站式學校服務，適合中小學及不同年齡層參與。",
        url: baseUrl,
        educationalCredentialAwarded: "Certificate of Completion",
        knowsAbout: [
          "一站式到校課程",
          "學校方案",
          "到校興趣班",
          "課後興趣班",
          "駐校開班",
          "學校到校服務",
          "手工藝到校課程",
          "蠟燭製作",
          "植物護理",
        ],
        offers: classes.slice(0, 5).map((classItem) => ({
          "@type": "Course",
          name: classItem.name,
          description: classItem.description,
          url: `${baseUrl}/classes/${classItem._id}`,
          provider: {
            "@type": "EducationalOrganization",
            name: "CosyLab",
          },
          courseCode: classItem._id,
          educationalLevel: "Beginner to Advanced",
          teaches: classItem.type === "candle" ? "蠟燭製作" : "植物護理",
          inLanguage: "zh-Hant",
          availableLanguage: ["zh-Hant", "en"],
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        url: baseUrl,
        name: "CosyLab- 一站式到校課程與學校方案",
        description:
          "一站式到校課程推薦：提供專業到校興趣班、學校方案、課後興趣班服務。包括蠟燭製作、植物護理等手工藝到校課程，可長期駐校開班。",
        inLanguage: "zh-Hant",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${baseUrl}/classes?search={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
    ];

    // Add Service schema for one-stop school program
    data.push({
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${baseUrl}/#school-service`,
      name: "一站式到校課程服務",
      description:
        "提供專業一站式到校課程、學校方案、課後興趣班服務。包括手工藝到校課程、長期駐校開班等完整學校服務方案。",
      provider: {
        "@type": "EducationalOrganization",
        "@id": `${baseUrl}/#educational`,
        name: "CosyLab",
      },
      areaServed: {
        "@type": "City",
        name: "Hong Kong",
      },
      serviceType: [
        "一站式到校課程",
        "學校方案",
        "到校興趣班",
        "課後興趣班",
        "駐校開班",
        "學校到校服務",
      ],
      audience: {
        "@type": "EducationalAudience",
        educationalRole: "student",
        audienceType: "中小學生、學校團體",
      },
    });

    // Add BreadcrumbList
    data.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "首頁",
          item: baseUrl,
        },
      ],
    });

    return data;
  }, [classes]);

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: `
        radial-gradient(ellipse at top left, rgba(139, 195, 74, 0.08) 0%, transparent 50%),
        radial-gradient(ellipse at bottom right, rgba(104, 159, 56, 0.06) 0%, transparent 50%),
        linear-gradient(180deg, rgba(250, 251, 248, 1) 0%, rgba(245, 248, 242, 1) 100%)
      `,
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(139, 195, 74, 0.02) 35px, rgba(139, 195, 74, 0.02) 70px),
          repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(104, 159, 56, 0.02) 35px, rgba(104, 159, 56, 0.02) 70px)
        `,
        pointerEvents: "none",
        zIndex: 0,
      }
    }}>
      <SEOHead
        title="一站式到校課程 學校方案 | CosyLab - 香港專業到校興趣班與工作坊"
        description="一站式到校課程推薦：CosyLab 提供專業到校興趣班、學校方案、課後興趣班服務。包括蠟燭製作、植物護理等手工藝到校課程，可長期駐校開班，適合中小學及成人團體。立即查詢一站式到校課程方案。"
        canonical="https://cosylabdesigns.com/"
        keywords="一站式到校課程, 學校方案, 到校興趣班, 到校工作坊, 學校到校服務, 課後興趣班, 駐校開班, 學校興趣班, 學校工作坊, 中小學到校課程, 到校手工藝課程, 到校蠟燭製作, 到校植物護理, 興趣班, 工作坊, 興趣班工作坊, 興趣班香港, 工作坊香港, 香港興趣班, 香港工作坊, 手工興趣班, 手工工作坊, 手工藝興趣班, 手工藝工作坊, 蠟燭製作興趣班, 蠟燭製作工作坊, 植物護理興趣班, 植物護理工作坊, 中小學興趣班, 中小學工作坊, 成人興趣班, 成人工作坊, 香港手工藝, 香港手工課程, 蠟燭製作課程, 植物護理課程, 手工藝班, 香港手工班, 中小學手工課程, 成人手工班, 手工創作, 香港craft classes, Hong Kong workshops, candle making Hong Kong, plant care Hong Kong, 香港手工藝課程推薦, 香港手工興趣班推薦, 香港工作坊推薦"
        structuredData={structuredData}
      />
      
      {/* Hero Section with Organic Flow */}
      <Box sx={{ 
        position: "relative",
        mb: { xs: 8, md: 12 },
        pt: { xs: 4, md: 8 },
        zIndex: 1,
      }}>
        <Container maxWidth="xl">
          <Box sx={{ 
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
            gap: { xs: 6, md: 8 },
            alignItems: "center",
            minHeight: slideshowHeight,
          }}>
            {/* Left Content */}
            <Slide direction="right" in={true} timeout={800}>
              <Box sx={{ 
                position: "relative",
                zIndex: 2,
                pl: { xs: 2, md: 4 },
              }}>
                <Box sx={{ 
                  display: "inline-flex",
                  alignItems: "center",
                  mb: 4,
                  px: 3,
                  py: 1,
                  backgroundColor: "rgba(139, 195, 74, 0.1)",
                  borderRadius: "50px",
                  border: "1px solid rgba(139, 195, 74, 0.2)",
                }}>
                  <SpaIcon sx={{ 
                    fontSize: 20, 
                    color: "#689F38", 
                    mr: 1.5,
                  }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: "#558B2F",
                      fontWeight: 500,
                      letterSpacing: 0.5,
                    }}
                  >
                    療癒手作體驗
                  </Typography>
                </Box>

                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontWeight: 300,
                    mb: 3,
                    fontSize: { xs: "3rem", sm: "3.5rem", md: "4.5rem" },
                    lineHeight: 1.1,
                    fontFamily: "'Georgia', serif",
                    background: "linear-gradient(135deg, #33691E 0%, #689F38 50%, #9CCC65 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: -0.5,
                  }}
                >
                  創造
                  <Box component="span" sx={{ fontWeight: 600 }}>療癒感</Box>
                  <br />
                  的手作時光
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    maxWidth: 500,
                    lineHeight: 1.7,
                    fontSize: { xs: "1.1rem", md: "1.2rem" },
                    color: "text.secondary",
                    mb: 5,
                    fontFamily: "'Source Serif Pro', serif",
                    fontWeight: 400,
                  }}
                >
                  從手作中尋找生活的慢節奏，讓每一次創作都成為療癒心靈的小確幸。
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={3}
                  sx={{ mb: 6 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate("/classes")}
                    sx={{
                      px: 5,
                      py: 2.5,
                      fontSize: "1.1rem",
                      borderRadius: "50px",
                      minWidth: 180,
                      backgroundColor: "#689F38",
                      color: "white",
                      boxShadow: "0 8px 32px rgba(104, 159, 56, 0.3)",
                      border: "2px solid transparent",
                      background: "linear-gradient(135deg, #689F38 0%, #8BC34A 100%)",
                      "&:hover": {
                        backgroundColor: "#558B2F",
                        boxShadow: "0 12px 40px rgba(104, 159, 56, 0.4)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                    endIcon={<ArrowForwardIcon />}
                  >
                    探索課程
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate("/about")}
                    sx={{
                      px: 5,
                      py: 2.5,
                      fontSize: "1.1rem",
                      borderRadius: "50px",
                      minWidth: 180,
                      borderColor: "rgba(104, 159, 56, 0.3)",
                      color: "#558B2F",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      backdropFilter: "blur(10px)",
                      "&:hover": {
                        borderColor: "#689F38",
                        backgroundColor: "rgba(139, 195, 74, 0.1)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    了解更多
                  </Button>
                </Stack>

                {/* Organic Elements */}
                <Box sx={{ 
                  display: "flex", 
                  gap: 4, 
                  mt: 4,
                  opacity: 0.8,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <LocalFloristIcon sx={{ 
                      fontSize: 24, 
                      color: "#8BC34A", 
                      mr: 1,
                      transform: "rotate(15deg)",
                    }} />
                    <Typography variant="body2" sx={{ color: "#689F38" }}>
                      精選材料
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <NatureIcon sx={{ 
                      fontSize: 24, 
                      color: "#689F38", 
                      mr: 1,
                      transform: "rotate(-10deg)",
                    }} />
                    <Typography variant="body2" sx={{ color: "#689F38" }}>
                      環保工藝
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Slide>

            {/* Right Slideshow */}
            <Slide direction="left" in={true} timeout={1000}>
              <Box sx={{ 
                position: "relative",
                borderRadius: { xs: "24px", md: "40px" },
                overflow: "hidden",
                boxShadow: "0 25px 80px rgba(104, 159, 56, 0.15)",
                transform: "rotate(2deg)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -2,
                  left: -2,
                  right: -2,
                  bottom: -2,
                  background: "linear-gradient(135deg, rgba(139, 195, 74, 0.3), rgba(104, 159, 56, 0.1))",
                  borderRadius: "inherit",
                  zIndex: -1,
                }
              }}>
                <ImageSlideshow
                  slides={slideshowData}
                  height={slideshowHeight}
                  autoPlay={true}
                  interval={5000}
                />
              </Box>
            </Slide>
          </Box>
        </Container>
      </Box>

      {/* Welcome Section */}
      <Container maxWidth="lg" sx={{ mb: { xs: 8, md: 12 }, zIndex: 1, position: "relative" }}>
        <Fade in={true} timeout={1200}>
          <Box>
            {/* Featured Classes */}
            <Box sx={{ mb: { xs: 8, md: 12 } }}>
              <Box textAlign="center" sx={{ mb: 8 }}>
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{
                    fontWeight: 300,
                    mb: 3,
                    fontSize: { xs: "2.5rem", md: "3rem" },
                    fontFamily: "'Georgia', serif",
                    background: "linear-gradient(135deg, #33691E 0%, #689F38 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: -0.5,
                  }}
                >
                  精選
                  <Box component="span" sx={{ fontWeight: 600 }}>工作坊</Box>
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.secondary",
                    maxWidth: 600,
                    mx: "auto",
                    lineHeight: 1.6,
                    fontFamily: "'Source Serif Pro', serif",
                  }}
                >
                  由專業導師精心設計，適合各級程度學員的優質課程,提供一站式到校課程服務
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: { xs: 4, md: 6 },
                }}
              >
                {featuredClasses.map((classItem, index) => (
                  <Fade in={true} timeout={1400 + index * 200} key={classItem._id}>
                    <Box>
                      <Paper
                        elevation={0}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          borderRadius: "24px",
                          overflow: "hidden",
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          backdropFilter: "blur(20px)",
                          border: "1px solid rgba(139, 195, 74, 0.1)",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          position: "relative",
                          "&:hover": {
                            transform: "translateY(-8px) rotate(-1deg)",
                            boxShadow: "0 20px 60px rgba(104, 159, 56, 0.15)",
                            border: "1px solid rgba(139, 195, 74, 0.2)",
                            "& .class-image": {
                              transform: "scale(1.08)",
                            },
                            "& .class-overlay": {
                              opacity: 1,
                            }
                          },
                        }}
                        onClick={() => navigate(`/classes/${classItem._id}`)}
                      >
                        <Box sx={{ position: "relative", overflow: "hidden" }}>
                          <Box
                            className="class-image"
                            component="img"
                            src={classItem.mainImage}
                            alt={classItem.name}
                            loading="lazy"
                            sx={{
                              width: "100%",
                              height: { xs: 220, md: 260 },
                              objectFit: "cover",
                              transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                          />
                          <Box
                            className="class-overlay"
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: "linear-gradient(180deg, transparent 0%, rgba(51, 105, 30, 0.1) 100%)",
                              opacity: 0,
                              transition: "opacity 0.3s ease",
                            }}
                          />
                          <Chip
                            label={classItem.type === "candle" ? "蠟燭製作" : "植物護理"}
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 20,
                              left: 20,
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              backdropFilter: "blur(10px)",
                              fontWeight: 600,
                              color: "#558B2F",
                              border: "1px solid rgba(139, 195, 74, 0.2)",
                              borderRadius: "20px",
                              px: 2,
                            }}
                          />
                        </Box>

                        <Box sx={{ flexGrow: 1, p: 4 }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 600,
                              mb: 2.5,
                              lineHeight: 1.3,
                              fontSize: { xs: "1.3rem", md: "1.4rem" },
                              color: "#2E7D32",
                              fontFamily: "'Georgia', serif",
                            }}
                          >
                            {classItem.name}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              mb: 3,
                              lineHeight: 1.7,
                              color: "text.secondary",
                              flexGrow: 1,
                              fontSize: { xs: "0.95rem", md: "1rem" },
                              fontFamily: "'Source Serif Pro', serif",
                            }}
                          >
                            {classItem.description}
                          </Typography>

                          <Divider sx={{ 
                            mb: 3, 
                            borderColor: "rgba(139, 195, 74, 0.2)",
                            opacity: 0.5,
                          }} />

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <AccessTimeIcon
                                sx={{ 
                                  fontSize: 20, 
                                  color: "#8BC34A", 
                                  mr: 1.5,
                                }}
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: "#689F38",
                                  fontWeight: 500,
                                }}
                              >
                                {classItem.details.duration}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor: "rgba(139, 195, 74, 0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.3s ease",
                              }}
                            >
                              <ArrowForwardIcon 
                                sx={{ 
                                  fontSize: 20, 
                                  color: "#689F38",
                                }} 
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Fade>
                ))}
              </Box>
            </Box>

            {/* Call to Action */}
            <Box
              sx={{
                py: { xs: 8, md: 10 },
                px: { xs: 4, md: 8 },
                background: `
                  linear-gradient(135deg, rgba(139, 195, 74, 0.08) 0%, rgba(104, 159, 56, 0.05) 100%),
                  radial-gradient(ellipse at center, rgba(255, 255, 255, 0.9) 0%, rgba(250, 251, 248, 0.95) 100%)
                `,
                borderRadius: { xs: "24px", md: "40px" },
                textAlign: "center",
                border: "2px solid rgba(139, 195, 74, 0.15)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "-50%",
                  left: "-50%",
                  width: "200%",
                  height: "200%",
                  background: `
                    repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(139, 195, 74, 0.03) 20px, rgba(139, 195, 74, 0.03) 40px),
                    repeating-linear-gradient(-45deg, transparent, transparent 20px, rgba(104, 159, 56, 0.03) 20px, rgba(104, 159, 56, 0.03) 40px)
                  `,
                }
              }}
            >
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography
                  variant="h3"
                  component="h3"
                  sx={{
                    fontWeight: 300,
                    mb: 3,
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    fontFamily: "'Georgia', serif",
                    background: "linear-gradient(135deg, #33691E 0%, #689F38 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: -0.5,
                  }}
                >
                  準備開始您的
                  <Box component="span" sx={{ fontWeight: 600 }}>創意之旅</Box>？
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 5,
                    color: "text.secondary",
                    maxWidth: 600,
                    mx: "auto",
                    lineHeight: 1.7,
                    fontFamily: "'Source Serif Pro', serif",
                  }}
                >
                  立即下載課程目錄，了解更多療癒手作課程資訊
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href={
                    portfolioPdf
                      ? portfolioPdf.startsWith("http")
                        ? portfolioPdf
                        : `${API_URL}${portfolioPdf}`
                      : "#"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={!portfolioPdf}
                  sx={{
                    px: 6,
                    py: 3,
                    fontSize: "1.1rem",
                    borderRadius: "50px",
                    minWidth: 280,
                    backgroundColor: "#689F38",
                    color: "white",
                    boxShadow: "0 10px 40px rgba(104, 159, 56, 0.3)",
                    border: "2px solid transparent",
                    background: "linear-gradient(135deg, #689F38 0%, #8BC34A 100%)",
                    "&:hover": {
                      backgroundColor: "#558B2F",
                      boxShadow: "0 15px 50px rgba(104, 159, 56, 0.4)",
                      transform: "translateY(-3px)",
                    },
                    "&:disabled": {
                      backgroundColor: "rgba(158, 158, 158, 0.3)",
                      color: "rgba(158, 158, 158, 0.5)",
                      boxShadow: "none",
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                  endIcon={<DownloadIcon />}
                >
                  下載課程目錄
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Container>

      <CitabilitySection routeKey="/" />
    </Box>
  );
};

export default Home;